import styled from 'styled-components';
import { MenuItem, TextField, useMediaQuery } from '@mui/material';
import { TFormValues } from '../../App';
import { StepContainer } from '../StepContainer';
import { Footnote } from '../Footnote';
import { SubTitle } from '../SubTitle';
import { Button } from '../Button';
import { Row } from '../Row';
import { breakpoints } from '../../constants';
import { IOption } from '../../types';

interface IProps {
    className?: string;
    formValues: TFormValues;
    options: IOption[];
    showDeleteDisclaimer: boolean;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    onStepButtonClick(option: IOption): void;
    setFieldValue(field: keyof TFormValues, value: string): void;
}

const BaseNameForm = ({
    className,
    formValues,
    options,
    showDeleteDisclaimer,
    getFieldError,
    onStepButtonClick,
    setFieldValue,
}: IProps) => {
    const mediumBreakpoint = useMediaQuery(`(${breakpoints.mediumMin})`);

    const {
        firstName,
        lastNamePrefix,
        lastName,
        salutation,
        phoneNumber,
        customerEmail,
    } = formValues;

    const subTitleText = showDeleteDisclaimer
        ? 'We hebben onderstaande contactgegevens nodig om je te identificeren zodat wij de juiste persoon uit onze systemen kunnen verwijderen.'
        : 'We zullen je gegevens enkel gebruiken voor het afhandelen van je vraag.';

    return (
        <StepContainer className={className}>
            <h3>
                Wat zijn je <strong>contactgegevens</strong>?
            </h3>
            <SubTitle>{subTitleText}</SubTitle>
            <TextField
                select
                error={!!getFieldError('salutation')}
                helperText={getFieldError('salutation')}
                id="salutation"
                label="Aanhef"
                name="salutation"
                sx={{ maxWidth: '140px' }}
                value={salutation}
                onChange={(e) => setFieldValue('salutation', e.target.value)}
            >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Dhr.">Dhr.</MenuItem>
                <MenuItem value="Mevr.">Mevr.</MenuItem>
            </TextField>
            <Row>
                <TextField
                    error={!!getFieldError('firstName')}
                    helperText={getFieldError('firstName')}
                    id="firstName"
                    label="Voornaam *"
                    value={firstName}
                    onChange={(e) => setFieldValue('firstName', e.target.value)}
                />
                <TextField
                    error={!!getFieldError('lastNamePrefix')}
                    helperText={getFieldError('lastNamePrefix')}
                    id="lastNamePrefix"
                    label="Tussenvoegsel"
                    sx={{
                        margin: `${mediumBreakpoint && '0 10px'}`,
                        width: `${mediumBreakpoint && '50%'}`,
                    }}
                    value={lastNamePrefix}
                    onChange={(e) =>
                        setFieldValue('lastNamePrefix', e.target.value)
                    }
                />
                <TextField
                    error={!!getFieldError('lastName')}
                    helperText={getFieldError('lastName')}
                    id="lastName"
                    label="Achternaam *"
                    value={lastName}
                    onChange={(e) => setFieldValue('lastName', e.target.value)}
                />
            </Row>
            <TextField
                error={!!getFieldError('phoneNumber')}
                helperText={getFieldError('phoneNumber')}
                id="phoneNumber"
                label="Telefoonnummer *"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setFieldValue('phoneNumber', e.target.value)}
            />
            <TextField
                error={!!getFieldError('customerEmail')}
                helperText={getFieldError('customerEmail')}
                id="customerEmail"
                label="E-mailadres *"
                type="email"
                value={customerEmail}
                onChange={(e) => setFieldValue('customerEmail', e.target.value)}
            />
            {options.map((option) => (
                <Button
                    key={option.title}
                    onClick={() => onStepButtonClick(option)}
                >
                    {option.title}
                </Button>
            ))}
            <Footnote>* Verplicht in te vullen</Footnote>
        </StepContainer>
    );
};

export const NameForm = styled(BaseNameForm)`
    ${Button} {
        width: 50%;
        margin-top: 24px;

        @media (${breakpoints.mediumMin}) {
            width: fit-content;
        }
    }

    ${Footnote} {
        margin-top: 24px;

        @media (${breakpoints.mediumMin}) {
            margin-left: auto;
            margin-top: 0;
        }
    }
`;
